<template>
  <div>
    <div
      class="flip-card"
      shadow
      @mouseover="isHovered = true"
      @mouseleave="isHovered = false"
      :style="`z-index: ${100 - index}`"
    >
      <div class="flip-card-inner">
        <!-- FLIP FRONT -->
        <div class="flip-card-front text-center">
          <IconWrapper class="mt-4" :icon="icon" />
          <h3
            class="collection-name d-flex align-items-center justify-content-center mx-auto"
          >
            <div>
              {{ label }}
              <TranslationTooltip
                v-if="!isTranslated"
                class="ml-1 d-inline-flex"
                :is-translated="isTranslated"
                :has-translated-contents="hasTranslatedContents"
              />
            </div>
          </h3>
        </div>

        <!-- FLIP BACK -->
        <div class="flip-card-back">
          <div slot="header">
            <div class="row header-row justify-content-between mx-1">
              <!-- EDIT COLLECTION NAME -->
              <input
                v-if="editLabel"
                alternative
                :maxlength="maxCaracter"
                placeholder="Nommer la branche"
                class="col-9 inputNodeLabel ls-1 pl-0 pt-O my-0"
                ref="label"
                v-autowidth="{
                  maxWidth: '960px',
                  minWidth: '50px',
                  comfortZone: 0,
                }"
                v-model="newLabel"
                @blur="
                  updateContent('label', newLabel);
                  editLabel = false;
                "
                @keyup.enter="$event.target.blur()"
                @input="notifyUserLength"
              />

              <!-- COLLECTION NAME -->
              <div v-else class="d-flex align-items-center">
                <IconWrapper
                  class="mini-icon"
                  :icon="icon"
                  :mini-icon="miniIcon"
                  :size="16"
                />
                <h3
                  class="card-title cursor ls-1 mb-0 px-0 pl-2"
                  @click="focusComponent(node.id)"
                >
                  {{ shortLabel }}
                </h3>
              </div>

              <div class="d-flex align-items-center">
                <TranslationTooltip
                  v-if="!isTranslated"
                  :is-translated="isTranslated"
                  :is-content="!!node.type"
                  :has-translated-contents="hasTranslatedContents"
                />
                <ParametricRootFolderCardOptions
                  :user-permissions="node.userPermissions"
                  @update-label="editCaseLabel()"
                  @update-icon="modals.icon = true"
                  @update-mini-icon="modals.miniIcon = true"
                  @add-child="modals.addItem = true"
                  @clone-kb="modals.cloneTo = true"
                  @delete-node="openDeleteModal"
                  @duplicate-in-same-location="
                    handleCloneToTarget({
                      caseName: node.label,
                      targetKnowledge: focusKnowledgeValue,
                      targetKnowledgeId: focusKnowledgeId,
                    })
                  "
                />
              </div>
            </div>
          </div>

          <!-- CARD BODY -->
          <div class="node-wraper" v-loading="isLoading">
            <parametric-root-children
              class="card-item"
              v-for="el in nodes"
              :key="el.key"
              :node="el"
              @focus-component="focusItem(el)"
              @update-content="$emit('update-content-published', $event)"
              @update-content-verification="updateContentVerification"
            />
            <div
              class="row cursor align-items-center mb-2"
              v-if="nodes.length == 0"
              @click="focusComponent(node.id)"
            >
              <div class="col-12 text-center">
                <img
                  src="~assets/empty-collection.png"
                  class="empty-collection-icon"
                />
                <p class="text-muted text-center">
                  <small>{{ $t('knowledge.root.empty-0') }}</small>
                </p>
              </div>
            </div>
          </div>

          <!-- FOOTER -->
          <div slot="footer">
            <div class="row footer-row justify-content-between mx-0">
              <div class="col-6">
                <p class="text-left mb-0 text-muted">
                  <small>
                    {{
                      `${nodes.length} ${$tc(
                        'knowledge.element',
                        nodes.length > 1 ? 2 : 1,
                      )}`
                    }}
                  </small>
                </p>
              </div>
              <el-tooltip
                :content="$t('knowledge.actions.display-all')"
                placement="top"
              >
                <a
                  class="col-auto cursor text-right text-muted"
                  @click="focusComponent(node.id)"
                >
                  <font-awesome-icon class="eye-icon" :icon="['fad', 'eye']" />
                </a>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ModalEvents
      modal-name="DeleteContentModal"
      :uid="deleteModalUniqueKey"
      @choice="deleteChild"
    />

    <IconEditionModal
      v-if="modals.icon || modals.miniIcon"
      :display="modals.icon || modals.miniIcon"
      :current-icon="focusedIcon"
      @choice="updateContentIcon($event)"
      @delete="handleDeleteIcon"
      @close="modals.icon = false"
    />
    <AddCollectionItemModal
      v-if="modals.addItem"
      :display="modals.addItem"
      @close="modals.addItem = false"
      @add-item="addItem($event)"
      @add-item-from-template="openTemplateModal"
    />

    <ModalEvents
      modal-name="AddTemplateModal"
      :uid="templateModalUniqueKey"
      @choice="addItemFromTemplate"
    />

    <CloneKnowledgeBaseModal
      v-if="modals.cloneTo"
      :display="modals.cloneTo"
      sourceType="case"
      :sourceLabel="label"
      :sourceId="node.id"
      @close="modals.cloneTo = false"
      @clone-to-target="handleCloneToTarget($event)"
    />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import IconWrapper from '@/components/IconWrapper';
import ParametricRootFolderCardOptions from './ParametricRootFolderCardOptions';
import ParametricRootChildren from './ParametricRootChildren';
import ModalEvents from '@/components/Modals/ModalEvents';
import IconEditionModal from 'components/Modals/IconEditionModal/IconEditionModal';
import AddCollectionItemModal from 'components/Modals/AddCollectionItemModal';
import CloneKnowledgeBaseModal from 'components/Modals/CloneKnowledgeBaseModal/CloneKnowledgeBaseModal';
import TranslationTooltip from '@/components/Commons/TranslationTooltip';

export default {
  name: 'parametric-root-folder-card',
  components: {
    IconWrapper,
    ParametricRootFolderCardOptions,
    ParametricRootChildren,
    ModalEvents,
    IconEditionModal,
    AddCollectionItemModal,
    CloneKnowledgeBaseModal,
    TranslationTooltip,
  },
  props: {
    node: {
      type: Object,
      default: () => ({
        content: {
          label: '',
        },
        type: '',
      }),
    },
    index: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      maxCaracter: 35,
      newLabel: '', // this.node.content.label,
      editLabel: false,
      isHovered: false,
      timer: '',
      isLoading: false,
      modals: {
        addItem: false,
        delete: false,
        icon: false,
        miniIcon: false,
        cloneTo: false,
      },
      resultCount: 0,
    };
  },
  computed: {
    deleteModalUniqueKey() {
      return `${this.$options.name}-${this.node.id}`;
    },
    templateModalUniqueKey() {
      return `${this.$options.name}-template-${this.node.id}`;
    },
    type() {
      return this.node.type;
    },
    content() {
      return this.node.children;
    },
    label() {
      return this.node.label && this.node.label.length > 50
        ? `${this.node.label.slice(0, 50)}...`
        : this.node.label;
    },
    shortLabel() {
      return this.node.label && this.node.label.length > 20
        ? `${this.node.label.slice(0, 20)}...`
        : this.node.label;
    },
    nodes() {
      return this.node.orderedChildrenAndContents || [];
    },
    icon() {
      return this.node.icon;
    },
    miniIcon() {
      return this.node.miniIcon;
    },
    focusedIcon() {
      let currentIcon = 'icon';
      if (this.modals.miniIcon) currentIcon = 'miniIcon';
      return this.node[currentIcon] && this.node[currentIcon].iconType == 'fa'
        ? this.node[currentIcon]
        : { iconType: 'fa', value: 'folder', color: null };
    },
    isTranslated() {
      return this.node.returnLanguage === this.navigationLanguage;
    },
    hasTranslatedContents() {
      return this.node.untranslatedWithTranslatedContents;
    },
    ...mapGetters('knowledgeModule', [
      'navigationLanguage',
      'focusKnowledgeId',
      'focusKnowledgeValue',
    ]),
  },
  watch: {
    editLabel(val) {
      if (val) this.newLabel = this.label;
    },
    isHovered(val) {
      if (val) {
        if (this.nodes && this.nodes.length) return;
        this.isLoading = true;
        this.timer = setTimeout(async () => {
          await this.getCaseById({
            caseId: this.node.id,
            updateFocusRootState: true,
          });
          this.isLoading = false;
        }, 200);
      } else {
        clearTimeout(this.timer);
      }
    },
  },
  methods: {
    ...mapActions('knowledgeModule', ['bulkUpdateStatus', 'getCaseById']),
    async openDeleteModal() {
      let loadingMessage = this.$message({
        duration: 0,
        dangerouslyUseHTMLString: true,
        iconClass: 'display: none',
        message: `<i class="fas fa-spinner fa-spin" style="margin-right: 1em;"></i> ${this.$t(
          'components.modals.delete-modal.loading',
        )}`,
      });
      this.resultCount =
        await this.$services.parametricContents.filterContentsCount(
          undefined, // knowledge name
          [], // product parameters
          [this.node.id],
          this.focusKnowledgeId,
        );
      loadingMessage.close();
      const payload = {
        component: 'DeleteContentModal',
        uid: this.deleteModalUniqueKey,
        props: {
          display: true,
          caseCount: this.resultCount,
          isCase: true,
        },
      };
      this.openModal(payload);
    },
    notifyUserLength() {
      if (this.newLabel.length === this.maxCaracter)
        this.$notify({
          title: 'Attention',
          message: 'Ne donnez pas des titres trop longs à vos collections',
          type: 'info',
        });
    },
    updateContent(type, value) {
      let payload;
      if (type === 'label') {
        payload = {
          label: value,
          title: value,
        };
      } else if (type === 'icon') {
        payload = { icon: value };
      } else if (type === 'published') {
        payload = value;
      }
      this.$emit('update-content', {
        id: this.content.id,
        payload,
      });
      this.modals.icon = false;
      this.editLabel = false;
    },
    updateContentIcon(icon) {
      let field = 'miniIcon';
      if (this.modals.icon) field = 'icon';
      this.$emit('update-content-icon', { icon, field });
      this.modals.icon = false;
      this.modals.miniIcon = false;
    },
    handleDeleteIcon() {
      let field = 'icon';
      if (this.modals.miniIcon) field = 'miniIcon';
      this.$emit('delete-content-icon', { field });
      this.modals.miniIcon = false;
      this.modals.icon = false;
    },
    editCaseLabel() {
      this.editLabel = true;
      this.$nextTick(() => this.$refs.label.focus());
    },
    addItem(item) {
      this.$emit('add-item', item);
      this.modals.addItem = false;
    },
    async openTemplateModal() {
      this.modals.addItem = false;
      const payload = {
        component: 'AddTemplateModal',
        uid: this.templateModalUniqueKey,
        props: {
          display: true,
        },
      };
      this.openModal(payload);
    },
    addItemFromTemplate(template) {
      const { label, type } = template.content;
      return this.$emit('add-item', {
        type,
        label,
        templateId: template.id,
      });
    },
    deleteChild(choice) {
      if (choice) this.$emit('delete-node');
    },
    handleCloneToTarget(payload) {
      this.$emit('clone-case-to-target', payload);
      this.modals.cloneTo = false;
    },
    focusComponent(childId) {
      const newCase = childId ? childId : this.node.id;
      this.$emit('focus-component', newCase);
    },
    focusItem(item) {
      switch (item.type) {
        case 'Article':
        case 'Diagnostic':
          this.$emit('focus-content', { content: item, caseRoute: this.node });
          break;
        default:
          this.$emit('focus-sub-component', item);
          break;
      }
    },
    async updateContentVerification({ id, isOutdated }) {
      await this.bulkUpdateStatus({
        contentIds: [id],
        key: 'isOutdated',
        value: isOutdated,
      });
    },
    ...mapActions('modalsModule', ['openModal']),
  },
};
</script>

<style lang="scss" scoped>
.card-item,
.eye-icon {
  &:hover {
    color: $blue-mayday;
  }
}

.node-wraper {
  overflow: auto;
  max-height: 124px;
  padding-left: 16px;
  padding-right: 16px;
}

.footer-row {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding-top: 4px;
  padding-bottom: 4px;
}

.header-row {
  padding: 4px;
  padding-left: 12px;
}

.home-card-icon {
  color: $grey-4-mayday;
  font-size: 48px;
  margin-top: 32px;
}

.collection-name {
  width: 200px;
  min-height: 100px;
  color: $grey-7-mayday;
}

.flip-card {
  background-color: transparent;
  width: 300px;
  height: 200px;
  perspective: 1000px;
  border-radius: 4px;
  // Fix options dropdown going behind other with dynamic z-index based on item's index
  position: relative;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;
}

.flip-card:hover .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden; /* Safari */
  backface-visibility: hidden;
}

.flip-card-front {
  background-color: white;
  color: black;
  box-shadow: 0px 5px 30px rgba(112, 144, 176, 0.2);
  border-radius: 4px;
}

.flip-card-back {
  background-color: white;
  color: black;
  transform: rotateY(180deg);
  box-shadow: 0px 5px 40px $shadow-4-mayday;
  border-radius: 4px;
}

.inputNodeLabel::placeholder {
  color: $grey-4-mayday;
}

.inputNodeLabel:focus,
.inputNodeLabel:active,
.inputNodeLabel {
  margin: 0px;
  padding: 0px;
  outline: none;
  border: none;
  font-family: inherit;
  font-size: 17px !important;
  font-weight: 600;
  line-height: 1.5;
  color: $blue-9-mayday;
}
.empty-collection-icon {
  max-width: 70px;
}

/* Content Style */
.card-icon {
  max-width: 40px;
}

.card-title {
  margin-bottom: 0px !important;
  word-break: break-word;
  color: $grey-7-mayday;
}

.diag .card-title:hover {
  color: $blue-mayday;
}

.article .card-title:hover {
  color: $yellow-mayday;
}

.folder .card-title:hover {
  filter: brightness(85%);
}
</style>
